// @ts-nocheck
import { jsPDF } from "jspdf";
import { useTranslation } from "react-i18next";
import { cancelReasonLabel } from "@kawkaw/shared";
import { Modal, Button } from "../components/admin-ui";
import { DownloadOutlined, PrinterOutlined, CloseOutlined } from "../components/admin-icons";
import type { TxnDetail } from "../api/hooks";

export type ReceiptTxn = TxnDetail & {
  order: TxnDetail["order"] & {
    kopitiam: { name: string; address: string; phone: string };
    items: Array<{ id: string; name: string; qty: number; price: string; options: unknown; note?: string | null }>;
  };
};

interface Props {
  open: boolean;
  onClose: () => void;
  txn: ReceiptTxn;
}

type ReceiptLine =
  | { type: "row"; label: string; value?: string; bold?: boolean }
  | { type: "text"; text: string; bold?: boolean; center?: boolean; small?: boolean }
  | { type: "divider" }
  | { type: "gap" };

const RECEIPT_ID = "kawkaw-receipt-printable";
const PAPER_MM = 80;
const LEFT_MM = 4;
const RIGHT_MM = 76;
const CENTER_MM = 40;
const LINE_HEIGHT_MM = 4.5;
const SMALL_LINE_HEIGHT_MM = 3.5;
const DASH_LINE = "-".repeat(43);
const PREVIEW_TEXT_STYLE = { overflowWrap: "anywhere" } as const;

function money(value: number | string | null | undefined): string {
  return `RM ${Number(value ?? 0).toFixed(2)}`;
}

function parseOptions(raw: unknown): Array<{ label: string; extra?: string }> {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw || "[]") : raw;
    return Array.isArray(parsed)
      ? parsed
          .map((option) => ({
            label: option?.group && option?.label ? `${option.group}: ${option.label}` : (option?.label ?? option?.name ?? option?.value ?? ""),
            extra: option?.extra_charge != null ? `+${Number(option.extra_charge).toFixed(2)}` : "",
          }))
          .filter((option) => option.label)
      : [];
  } catch {
    return [];
  }
}

function cleanItemNote(note: string | null | undefined): string {
  return note?.split(" | ").map((part) => part.includes(": ") ? part.slice(part.indexOf(": ") + 2) : part).join(", ") ?? "";
}

function parseWalkInNote(note: string | null | undefined): string {
  return note?.startsWith("Walk-in") ? note.split("—").slice(1).join("—").trim() : "";
}

function parseDelivery(address: string | null | undefined) {
  if (!address) return null;
  const [geo = "", unit = "", note = ""] = address.split("||").map((part) => part.trim());
  return { line1: unit ? `${unit}, ${geo}` : (geo || address), note };
}

function getReceiptData(txn: ReceiptTxn) {
  const order = txn.order;
  return {
    order,
    kopitiam: order.kopitiam,
    contactName: order.customer_name ?? order.customer?.name ?? "",
    contactPhone: order.customer_phone ?? order.customer?.phone ?? "",
    orderNote: parseWalkInNote(order.special_note),
    delivery: order.type === "delivery" ? parseDelivery(order.address) : null,
    orderTime: new Date(order.created_at ?? txn.created_at).toLocaleString("en-MY", { hour12: false }),
    printedTime: new Date().toLocaleString("en-MY", { hour12: false }),
    subtotal: Number(order.subtotal ?? 0),
    sstAmount: Number(order.sst_amount ?? 0),
    sstIncluded: !!(order as { sst_included?: boolean }).sst_included,   // customer-app orders: prices already contain SST
    deliveryFee: Number(order.delivery_fee ?? 0),
    discount: Number(order.voucher_discount ?? 0),
    total: Number(order.total ?? txn.amount ?? 0),
    paidAmount: Number(txn.amount ?? order.total ?? 0),
  };
}

function contactLines(data: ReturnType<typeof getReceiptData>): ReceiptLine[] {
  const lines: ReceiptLine[] = [];
  if (data.contactName) lines.push({ type: "text", text: `Name: ${data.contactName.toUpperCase()}`, bold: true });
  if (data.contactPhone) lines.push({ type: "text", text: `Phone: ${data.contactPhone}`, small: true });
  return lines;
}

function receiptLines(txn: ReceiptTxn): ReceiptLine[] {
  const data = getReceiptData(txn);
  const lines: ReceiptLine[] = [
    { type: "text", text: (data.kopitiam.name || "KAWKAW KOPITIAM").toUpperCase(), bold: true, center: true },
    ...(data.kopitiam.address ? [{ type: "text", text: data.kopitiam.address, center: true, small: true }] : []),
    ...(data.kopitiam.phone ? [{ type: "text", text: `Tel: ${data.kopitiam.phone}`, center: true, small: true }] : []),
    { type: "divider" },
    { type: "row", label: "ORDER", value: data.order.order_code },
    { type: "row", label: "TYPE", value: String(data.order.type).toUpperCase() },
    { type: "row", label: "TIME", value: data.orderTime },
    { type: "divider" },
  ];

  if (data.order.type !== "delivery" && (data.contactName || data.contactPhone || data.orderNote)) {
    lines.push(...contactLines(data));
    if (data.orderNote) lines.push({ type: "text", text: `Note: ${data.orderNote.toUpperCase()}`, small: true });
    lines.push({ type: "divider" });
  }

  if (data.delivery) {
    lines.push(
      ...contactLines(data),
      { type: "text", text: `DELIVERY: ${data.delivery.line1.toUpperCase()}`, small: true },
    );
    if (data.delivery.note) lines.push({ type: "text", text: `NOTE: ${data.delivery.note.toUpperCase()}`, small: true });
    lines.push({ type: "divider" });
  }

  if (data.order.cancel_reason) {
    lines.push(
      { type: "text", text: "CANCELLATION REASON", bold: true, center: true },
      { type: "text", text: cancelReasonLabel(data.order.cancel_reason, "en"), center: true, small: true },
      { type: "divider" },
    );
  }

  for (const item of data.order.items) {
    lines.push({ type: "row", label: `${item.qty}x ${item.name}`, value: (Number(item.price) * item.qty).toFixed(2) });
    parseOptions(item.options).forEach((option) => lines.push({ type: "row", label: `  + ${option.label}`, value: option.extra ?? "" }));
    const note = cleanItemNote(item.note);
    if (note) lines.push({ type: "text", text: `  Note: ${note}`, small: true });
  }

  lines.push(
    { type: "divider" },
    { type: "row", label: "Subtotal", value: money(data.subtotal) },
  );
  if (data.sstAmount > 0 && !data.sstIncluded) {
    const sstRatePct = data.subtotal > 0 ? Math.round((data.sstAmount / data.subtotal) * 100) : 0;
    lines.push({ type: "row", label: `SST ${sstRatePct}%`, value: money(data.sstAmount) });
  }
  if (data.order.type === "delivery" && data.deliveryFee > 0) lines.push({ type: "row", label: "Delivery Fee", value: money(data.deliveryFee) });
  if (data.discount > 0) lines.push({ type: "row", label: data.order.voucher_code ? `Discount (${data.order.voucher_code})` : "Discount", value: `-${money(data.discount)}` });

  lines.push(
    { type: "row", label: "TOTAL", value: money(data.total), bold: true },
    ...(data.sstAmount > 0 && data.sstIncluded ? [{ type: "row" as const, label: "(Total includes SST)", value: money(data.sstAmount) }] : []),
    { type: "divider" },
    { type: "row", label: `PAID ${String(txn.method).toUpperCase()}`, value: money(data.paidAmount), bold: true },
  );
  if (txn.reference) lines.push({ type: "text", text: `REF: ${txn.reference}`, small: true });

  lines.push(
    { type: "divider" },
    { type: "gap" },
    { type: "text", text: "Thank you. Come again!", bold: true, center: true },
    { type: "text", text: `Printed: ${data.printedTime}`, center: true },
  );

  return lines;
}

function renderPdfLines(doc: jsPDF, lines: ReceiptLine[]): number {
  let y = 8;
  const setFont = (bold = false, small = false) => {
    doc.setFont("Courier", bold ? "bold" : "normal");
    doc.setFontSize(small ? 8 : bold ? 10 : 9);
  };

  for (const line of lines) {
    if (line.type === "gap") {
      y += 2;
      continue;
    }
    if (line.type === "divider") {
      setFont(false, true);
      doc.text(DASH_LINE, LEFT_MM, y);
      y += LINE_HEIGHT_MM;
      continue;
    }
    if (line.type === "row") {
      setFont(line.bold);
      const labelLines = doc.splitTextToSize(line.label, 48) as string[];
      doc.text(labelLines, LEFT_MM, y);
      doc.text(line.value ?? "", RIGHT_MM, y, { align: "right" });
      y += Math.max(1, labelLines.length) * LINE_HEIGHT_MM;
      continue;
    }

    setFont(line.bold, line.small);
    const textLines = doc.splitTextToSize(line.text, RIGHT_MM - LEFT_MM - 4) as string[];
    doc.text(textLines, line.center ? CENTER_MM : LEFT_MM, y, line.center ? { align: "center" } : undefined);
    y += textLines.length * (line.small ? SMALL_LINE_HEIGHT_MM : LINE_HEIGHT_MM);
  }

  return y + 4;
}

function buildThermalReceiptPdf(txn: ReceiptTxn): jsPDF {
  const lines = receiptLines(txn);
  const measurementDoc = new jsPDF({ unit: "mm", format: [PAPER_MM, 1000] });
  const contentHeight = Math.ceil(renderPdfLines(measurementDoc, lines));
  const doc = new jsPDF({ unit: "mm", format: [PAPER_MM, Math.max(45, contentHeight)] });
  renderPdfLines(doc, lines);
  return doc;
}

export function downloadReceipt(txn: ReceiptTxn) {
  buildThermalReceiptPdf(txn).save(`receipt-${txn.order.order_code}.pdf`);
}

function printReceipt(txn: ReceiptTxn) {
  const doc = buildThermalReceiptPdf(txn);
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
}

function PreviewLine({ line }: { line: ReceiptLine }) {
  if (line.type === "gap") return <div style={{ height: "2mm" }} />;
  if (line.type === "divider") return <div style={{ margin: "2mm 0", whiteSpace: "pre", overflow: "hidden" }}>{DASH_LINE}</div>;
  if (line.type === "row") {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", gap: "3mm", minHeight: "4.5mm", fontWeight: line.bold ? 800 : 400 }}>
        <span style={{ ...PREVIEW_TEXT_STYLE, maxWidth: "52mm" }}>{line.label}</span>
        <span style={{ flexShrink: 0 }}>{line.value}</span>
      </div>
    );
  }
  return (
    <div
      style={{
        ...PREVIEW_TEXT_STYLE,
        textAlign: line.center ? "center" : "left",
        fontSize: line.small ? "8pt" : line.bold ? "12pt" : "9pt",
        fontWeight: line.bold ? 800 : 400,
        textTransform: line.bold && line.center ? "uppercase" : "none",
      }}
    >
      {line.text}
    </div>
  );
}

function ThermalReceiptPreview({ txn }: { txn: ReceiptTxn }) {
  return (
    <div
      id={RECEIPT_ID}
      style={{
        width: "80mm",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#fff",
        color: "#111827",
        fontFamily: "Courier, monospace",
        fontSize: "9pt",
        lineHeight: 1.35,
        padding: "8mm 4mm",
        boxSizing: "border-box",
        boxShadow: "0 14px 36px rgba(15,23,42,0.12)",
      }}
    >
      {receiptLines(txn).map((line, index) => <PreviewLine key={index} line={line} />)}
    </div>
  );
}

export function ReceiptModal({ open, onClose, txn }: Props) {
  const { t } = useTranslation();
  const actions = [
    { icon: <DownloadOutlined />, label: t("receipt.downloadPdf"), onClick: () => downloadReceipt(txn) },
    { icon: <PrinterOutlined />, label: t("receipt.printReceipt"), onClick: () => printReceipt(txn), primary: true },
    { icon: <CloseOutlined />, label: t("receipt.close"), onClick: onClose, text: true },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} width="min(92vw, 520px)" closable={false}>
      <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "28px 24px", background: "#f3f4f6" }}>
        <ThermalReceiptPreview txn={txn} />
      </div>

      <div
        style={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: "16px 20px",
          borderTop: "1px solid #F3F4F6",
          background: "#fff",
          zIndex: 2,
        }}
      >
        {actions.map((action) => (
          <Button
            key={action.label}
            type={action.primary ? "primary" : action.text ? "text" : undefined}
            icon={action.icon}
            onClick={action.onClick}
            style={{
              borderRadius: action.text ? undefined : 24,
              height: action.text ? undefined : 40,
              paddingInline: action.text ? undefined : 20,
              fontWeight: 600,
              color: action.text ? "#534434" : undefined,
              background: action.primary ? "#006c49" : undefined,
              borderColor: action.primary ? "#006c49" : undefined,
            }}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </Modal>
  );
}
